import { OwnerId } from 'prove-common'
import {
  findByOwnerPath,
  hasTerminalDelegationAncestor,
  sumSamplesTaken,
  type DelegationNode
} from '../../utils/taskDelegationUtils'
import { isDelegationPathHidden } from '../../utils/delegationVisibility'
import type { OrgLeaf } from '../delegation-explorer/orgTree'

export type WorksheetRow = OrgLeaf & {
  key: string
  plannedQuantity: number
  samplesTaken: number
  active: boolean
  canEdit: boolean
  blockedByLegacyDelegation: boolean
}

/** Merge current organization leaves with persisted legacy/inactive targets. */
export const buildWorksheetRows = (
  delegations: DelegationNode[],
  orgLeaves: OrgLeaf[],
  rootPath: OwnerId[],
  hiddenUnits: ReadonlySet<string>,
  canEditQuantity: (ownerId: OwnerId) => boolean
): WorksheetRow[] => {
  const visibleLeaves = orgLeaves.filter(
    (leaf) => !isDelegationPathHidden(leaf.path, hiddenUnits)
  )
  const namesByPath = new Map<string, string[]>()
  for (const leaf of visibleLeaves) {
    leaf.path.forEach((_, index) => {
      namesByPath.set(
        JSON.stringify(leaf.path.slice(0, index + 1)),
        leaf.names.slice(0, index + 1)
      )
    })
  }
  const leafKeys = new Set(
    visibleLeaves.map((leaf) => JSON.stringify(leaf.path))
  )
  const toRow = (
    leaf: OrgLeaf,
    active: boolean,
    delegation?: DelegationNode
  ): WorksheetRow => {
    const blockedByLegacyDelegation = hasTerminalDelegationAncestor(
      delegations,
      leaf.path
    )
    return {
      ...leaf,
      key: JSON.stringify(leaf.path),
      plannedQuantity: delegation?.plannedQuantity ?? 0,
      samplesTaken: delegation ? sumSamplesTaken(delegation) : 0,
      active,
      canEdit:
        active &&
        canEditQuantity(leaf.path.at(-1)!) &&
        !blockedByLegacyDelegation,
      blockedByLegacyDelegation
    }
  }

  return [
    ...visibleLeaves
      .filter((leaf) => leaf.path.length > 0)
      .map((leaf) =>
        toRow(leaf, true, findByOwnerPath(delegations, leaf.path))
      ),
    ...collectDelegationLeafPaths(delegations)
      .filter(
        ({ path }) =>
          path.length >= rootPath.length &&
          !isDelegationPathHidden(path, hiddenUnits) &&
          !leafKeys.has(JSON.stringify(path))
      )
      .map(({ path, delegation }) => {
        const names = namesByPath.get(JSON.stringify(path))
        return toRow(
          { path, names: names ?? path.map((owner) => owner.owner) },
          names != null,
          delegation
        )
      })
  ]
}

export type DelegationLeafPath = {
  path: OwnerId[]
  delegation: DelegationNode
}

export const collectDelegationLeafPaths = (
  roots: DelegationNode[]
): DelegationLeafPath[] => {
  const walk = (
    nodes: DelegationNode[],
    parentPath: OwnerId[]
  ): DelegationLeafPath[] =>
    nodes.flatMap((node) => {
      const path = [...parentPath, node.ownerId]
      return node.children.length === 0
        ? [{ path, delegation: node }]
        : walk(node.children, path)
    })
  return walk(roots, [])
}

export type PasteTarget = {
  key: string
  minimum: number
  active: boolean
  editable: boolean
}

export type PasteRow = {
  line: number
  ownerId: string
  quantity?: number
  error?: string
}

type ClipboardEntry = {
  ownerId: unknown
  plannedQuantity: unknown
}

type DelegationClipboard = {
  version: unknown
  entries: unknown
}

export const parseDelegationPaste = (
  value: string,
  targets: Map<string, PasteTarget>
): PasteRow[] => {
  const seen = new Set<string>()
  let clipboard: DelegationClipboard
  try {
    clipboard = JSON.parse(value) as DelegationClipboard
  } catch {
    return [{ line: 1, ownerId: '', error: 'Ugyldig JSON-format' }]
  }

  if (clipboard.version !== 1 || !Array.isArray(clipboard.entries)) {
    return [{ line: 1, ownerId: '', error: 'Ugyldig fordelingsformat' }]
  }

  return clipboard.entries.map((entry, index) => {
    const { ownerId, plannedQuantity } = (entry ?? {}) as ClipboardEntry
    const validOwnerId = typeof ownerId === 'string' ? ownerId : ''
    const quantity = plannedQuantity
    const target = targets.get(validOwnerId)
    let error: string | undefined
    if (OwnerId.fromString(validOwnerId) == null)
      error = 'Ugyldig format eller ownerId'
    else if (!Number.isInteger(quantity) || (quantity as number) < 0)
      error = 'Antall må være et heltall på 0 eller mer'
    else if (seen.has(validOwnerId)) error = 'Duplikat ownerId'
    else if (target == null) error = 'Ukjent ownerId'
    else if (!target.active) error = 'Enheten er inaktiv'
    else if (!target.editable) error = 'Mangler tilgang til å endre enheten'
    else if ((quantity as number) < target.minimum)
      error = `Kan ikke være lavere enn ${target.minimum}`
    seen.add(validOwnerId)
    return {
      line: index + 1,
      ownerId: validOwnerId,
      quantity: typeof quantity === 'number' ? quantity : undefined,
      error
    }
  })
}
